/**
 * Active Organization Accessor
 *
 * Single source of truth for reading, writing, and clearing the
 * active-organization ID persisted in `localStorage`. Every other module
 * that needs the active org ID goes through these functions instead of
 * touching `localStorage` directly — the `'activeOrgId'` key literal and
 * its storage-unavailable (SSR, private browsing) handling live here only.
 */

import { logger } from './logger';

const ACTIVE_ORG_ID_KEY = 'activeOrgId';

/**
 * Remove the persisted active-organization ID.
 *
 * @example
 * ```typescript
 * clearActiveOrgId(); // e.g. on logout, or on login/onboarding mount
 * ```
 */
export function clearActiveOrgId(): void {
  try {
    localStorage.removeItem(ACTIVE_ORG_ID_KEY);
  } catch (error) {
    logger.warn({ component: 'activeOrg' }, 'Failed to clear activeOrgId from localStorage', {
      error,
    });
  }
}

/**
 * Read the persisted active-organization ID.
 *
 * @returns The stored org ID, or `null` if unset or if `localStorage` is
 * unavailable (e.g. SSR, private browsing).
 *
 * @example
 * ```typescript
 * const orgId = getActiveOrgId();
 * if (orgId) {
 *   headers['X-Organization-ID'] = orgId;
 * }
 * ```
 */
export function getActiveOrgId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_ID_KEY);
  } catch (error) {
    logger.warn({ component: 'activeOrg' }, 'Failed to read activeOrgId from localStorage', {
      error,
    });
    return null;
  }
}

/**
 * Persist the active-organization ID.
 *
 * Write-only — does not update any React state or trigger navigation.
 * Callers that need a reload after switching organizations call
 * `window.location.assign` themselves after calling this.
 *
 * @param orgId - The organization ID to persist
 *
 * @example
 * ```typescript
 * setActiveOrgId(orgId);
 * window.location.assign('/dashboard');
 * ```
 */
export function setActiveOrgId(orgId: string): void {
  try {
    localStorage.setItem(ACTIVE_ORG_ID_KEY, orgId);
  } catch (error) {
    logger.warn({ component: 'activeOrg' }, 'Failed to persist activeOrgId to localStorage', {
      error,
    });
  }
}
